import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CarFront, Plus, Trash2 } from 'lucide-react'
import { useCreateTrip } from './useTrips'
import { addPlace } from '../../shared/api/places'
import { addPoint } from '../../shared/api/points'
import { Button } from '../../shared/components/Button'

type Stay = { name: string; address: string; checkIn: string; checkOut: string }

function dayIndex(start: string, date: string): number {
  return Math.round((Date.parse(`${date}T12:00:00Z`) - Date.parse(`${start}T12:00:00Z`)) / 86_400_000)
}

export function RoadTripCreate() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create = useCreateTrip()
  const [title, setTitle] = useState('')
  const [destination, setDestination] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [zone, setZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC')
  const [pickup, setPickup] = useState('')
  const [pickupAt, setPickupAt] = useState('')
  const [returnPlace, setReturnPlace] = useState('')
  const [returnAt, setReturnAt] = useState('')
  const [stays, setStays] = useState<Stay[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [createdId, setCreatedId] = useState('')

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (createdId) { navigate(`/trips/${createdId}/places`); return }
    if (!title.trim() || !start || !end || end < start || !pickup.trim() || !returnPlace.trim() ||
      !pickupAt || !returnAt || pickupAt.slice(0, 10) < start || returnAt.slice(0, 10) > end ||
      returnAt < pickupAt || stays.some(s => !s.name.trim() || !s.checkIn || !s.checkOut ||
        s.checkIn < start || s.checkOut > end || s.checkOut <= s.checkIn)) {
      setError(t('road_trip.invalid'))
      return
    }
    setBusy(true)
    try {
      const trip = await create.mutateAsync({ title: title.trim(), destination: destination.trim(),
        start_date: start, end_date: end, time_zone_id: zone })
      setCreatedId(trip.id)
      const carEvents = [
        { name: t('road_trip.pickup'), address: pickup, at: pickupAt },
        { name: t('road_trip.return'), address: returnPlace, at: returnAt },
      ]
      for (const event of carEvents) {
        const place = await addPlace(trip.id, { title: event.name, type: 'station', address: event.address })
        await addPoint(trip.id, { place_id: place.id, title: place.title, type: place.type,
          address: place.address, day_index: dayIndex(start, event.at.slice(0, 10)),
          date: event.at.slice(0, 10), time: event.at.slice(11, 16), timing: 'exact',
          duration_minutes: 0, priority: 'must' })
      }
      for (const stay of stays) {
        const place = await addPlace(trip.id, { title: stay.name.trim(), type: 'hotel', address: stay.address.trim() })
        await addPoint(trip.id, { place_id: place.id, title: place.title, type: 'hotel', address: place.address,
          day_index: dayIndex(start, stay.checkIn), timing: 'stay',
          window_start: stay.checkIn, window_end: stay.checkOut, duration_minutes: 0, priority: 'must' })
      }
      navigate(`/trips/${trip.id}/places`)
    } catch (err) { setError(err instanceof Error ? err.message : String(err)) }
    finally { setBusy(false) }
  }

  return <div className="mx-auto max-w-2xl space-y-5">
    <header className="flex items-center gap-2"><CarFront className="h-7 w-7 text-st-primary" />
      <div><h1 className="text-2xl font-bold">{t('road_trip.title')}</h1>
        <p className="text-sm text-st-muted">{t('road_trip.intro')}</p></div></header>
    <form onSubmit={e => void submit(e)} className="space-y-4">
      <div className="grid gap-3 rounded border border-st-line bg-st-surface p-4 sm:grid-cols-2">
        <label className="text-sm">{t('road_trip.trip_name')}<input className="input" value={title} onChange={e => setTitle(e.target.value)} required /></label>
        <label className="text-sm">{t('road_trip.geography')}<input className="input" value={destination} onChange={e => setDestination(e.target.value)} /></label>
        <label className="text-sm">{t('road_trip.start')}<input className="input" type="date" value={start} onChange={e => setStart(e.target.value)} required /></label>
        <label className="text-sm">{t('road_trip.end')}<input className="input" type="date" value={end} onChange={e => setEnd(e.target.value)} required /></label>
        <label className="text-sm sm:col-span-2">{t('manual.zone')}<input className="input" value={zone} onChange={e => setZone(e.target.value)} required /></label>
      </div>
      <div className="grid gap-3 rounded border border-st-line bg-st-surface p-4 sm:grid-cols-2">
        <label className="text-sm">{t('road_trip.pickup_place')}<input className="input" value={pickup} onChange={e => setPickup(e.target.value)} required /></label>
        <label className="text-sm">{t('road_trip.pickup_at')}<input className="input" type="datetime-local" value={pickupAt} onChange={e => setPickupAt(e.target.value)} required /></label>
        <label className="text-sm">{t('road_trip.return_place')}<input className="input" value={returnPlace} onChange={e => setReturnPlace(e.target.value)} required /></label>
        <label className="text-sm">{t('road_trip.return_at')}<input className="input" type="datetime-local" value={returnAt} onChange={e => setReturnAt(e.target.value)} required /></label>
      </div>
      <section className="space-y-3 rounded border border-st-line bg-st-surface p-4">
        <div className="flex items-center justify-between gap-2"><h2 className="font-semibold">{t('road_trip.stays')}</h2>
          <Button type="button" variant="outline" onClick={() => setStays([...stays, { name: '', address: '', checkIn: '', checkOut: '' }])}>
            <Plus className="h-4 w-4" /> {t('road_trip.add_stay')}</Button></div>
        {stays.map((stay, i) => <div key={i} className="grid gap-2 border-t border-st-line pt-3 sm:grid-cols-2">
          {(['name', 'address', 'checkIn', 'checkOut'] as const).map(field => <label key={field} className="text-sm">
            {t(`road_trip.${field}`)}<input className="input" type={field === 'checkIn' || field === 'checkOut' ? 'date' : 'text'}
              value={stay[field]} onChange={e => setStays(stays.map((s, n) => n === i ? { ...s, [field]: e.target.value } : s))} /></label>)}
          <button type="button" className="flex items-center gap-1 text-sm text-st-danger" onClick={() => setStays(stays.filter((_, n) => n !== i))}>
            <Trash2 className="h-4 w-4" /> {t('road_trip.remove_stay')}</button></div>)}
      </section>
      {error && <p role="alert" className="text-sm text-st-danger">{error}</p>}
      {createdId && <a className="text-sm text-st-primary underline" href={`/trips/${createdId}/places`}>{t('road_trip.open_partial')}</a>}
      <Button type="submit" variant="accent" disabled={busy}>{busy ? t('road_trip.creating') : createdId ? t('road_trip.open_partial') : t('road_trip.create')}</Button>
    </form>
  </div>
}
